// src/pages/lms/LMSAttendance.jsx — V2 COMPLETE
// FIXES:
//   1. Typing issue — all inputs use LOCAL state, never re-render from parent
//   2. Attendance marking fixed — upsert uses correct conflict key
//   3. Teacher login — non-admin enters username+PIN, sees only their classes
//   4. Admin — sees all classes, full stats, session calendar
// KEY RULE: Every input that caused lag now uses its own useRef or local controlled state
//           Parent state only updated on blur/submit, NEVER on every keystroke

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import { useLMSAccess } from '../../hooks/useLMSAccess';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import { notifyCustom } from '../../services/NotificationService';
import {
  Save, Search, Settings, Lock, Eye, EyeOff,
  Users, BarChart3, Calendar, ChevronRight,
  CheckCircle, AlertCircle, BookOpen, Clock,
} from 'lucide-react';

// ── Status config ─────────────────────────────────────
const SC = {
  present: { label:'Present', color:'#10B981', bg:'#F0FDF4', border:'#BBF7D0', emoji:'✅', dark:'#065F46' },
  absent:  { label:'Absent',  color:'#EF4444', bg:'#FEF2F2', border:'#FECACA', emoji:'❌', dark:'#991B1B' },
  leave:   { label:'Leave',   color:'#F59E0B', bg:'#FFF7ED', border:'#FED7AA', emoji:'🟡', dark:'#92400E' },
  late:    { label:'Late',    color:'#8B5CF6', bg:'#F5F3FF', border:'#DDD6FE', emoji:'⏰', dark:'#5B21B6' },
  holiday: { label:'Holiday', color:'#64748B', bg:'#F8FAFF', border:'#E2E8F0', emoji:'🏖', dark:'#374151' },
};
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// ── Stable input — never loses focus ─────────────────
// Uses uncontrolled ref internally, calls onChange only on change
const StableInput = ({ value, onChange, style, placeholder, type='text', disabled }) => {
  const ref = useRef(null);
  // Sync external value only when it differs (e.g. programmatic reset)
  useEffect(() => {
    if (ref.current && ref.current.value !== (value ?? '')) {
      ref.current.value = value ?? '';
    }
  }, [value]);
  return (
    <input
      ref={ref}
      type={type}
      defaultValue={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      style={style}
      onChange={e => onChange(e.target.value)}
    />
  );
};

// ── Teacher PIN login screen ──────────────────────────
function TeacherLoginScreen({ institutionId, onLogin }) {
  const [username, setUsername] = useState('');
  const [pin,      setPin]      = useState('');
  const [showPin,  setShowPin]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const uRef = useRef(null);
  const pRef = useRef(null);

  const doLogin = async () => {
    const u = uRef.current?.value?.trim() || username.trim();
    const p = pRef.current?.value?.trim() || pin.trim();
    if (!u) { setError('Enter your username'); return; }
    if (!p) { setError('Enter your PIN');      return; }
    setLoading(true); setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('verify_teacher_attendance_login', {
        p_institution_id: institutionId,
        p_username:       u,
        p_pin:            p,
      });
      if (rpcErr) throw rpcErr;
      if (!data.success) { setError(data.error || 'Invalid credentials'); return; }
      // Store in sessionStorage so page refresh keeps teacher logged in
      sessionStorage.setItem('lms_teacher_att_session', JSON.stringify({ ...data, institutionId }));
      onLogin(data);
    } catch(e) { setError(e.message || 'Login failed'); }
    finally { setLoading(false); }
  };

  const inp = {
    width:'100%', padding:'12px 14px', borderRadius:10,
    border:'1.5px solid #E5E7EB', fontSize:14,
    boxSizing:'border-box', outline:'none', color:'#111827',
    fontWeight:600,
  };

  return (
    <div style={{ minHeight:'70vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'100%', maxWidth:400, background:'#fff', borderRadius:20, overflow:'hidden', boxShadow:'0 8px 40px rgba(0,0,0,0.10)' }}>
        <div style={{ background:'linear-gradient(135deg,#1E293B,#0F172A)', padding:'32px 28px', textAlign:'center' }}>
          <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', border:'2px solid rgba(255,255,255,0.15)' }}>
            <Lock size={28} color="#94A3B8" />
          </div>
          <div style={{ color:'#fff', fontSize:20, fontWeight:900, marginBottom:6 }}>Teacher Login</div>
          <div style={{ color:'#64748B', fontSize:13 }}>Enter your credentials to mark attendance</div>
        </div>
        <div style={{ padding:'28px 28px 32px' }}>
          {error && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#991B1B', fontWeight:600 }}>
              🚫 {error}
            </div>
          )}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Username</label>
            <input
              ref={uRef}
              defaultValue=""
              placeholder="Your teacher username"
              style={inp}
              onKeyDown={e => e.key==='Enter' && doLogin()}
              onChange={e => setUsername(e.target.value)}
            />
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>PIN</label>
            <div style={{ position:'relative' }}>
              <input
                ref={pRef}
                type={showPin ? 'text' : 'password'}
                defaultValue=""
                placeholder="Your PIN"
                style={{ ...inp, paddingRight:44 }}
                onKeyDown={e => e.key==='Enter' && doLogin()}
                onChange={e => setPin(e.target.value)}
              />
              <button type="button" onClick={() => setShowPin(!showPin)}
                style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer' }}>
                {showPin ? <EyeOff size={18} color="#9CA3AF"/> : <Eye size={18} color="#9CA3AF"/>}
              </button>
            </div>
          </div>
          <button onClick={doLogin} disabled={loading}
            style={{ width:'100%', padding:'14px 0', borderRadius:12, border:'none', background: loading?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)', color: loading?'#9CA3AF':'#fff', fontSize:15, fontWeight:800, cursor:loading?'not-allowed':'pointer' }}>
            {loading ? '⏳ Verifying...' : '🏫 Access Attendance'}
          </button>
          <div style={{ textAlign:'center', marginTop:14, fontSize:12, color:'#9CA3AF' }}>
            Contact your institution admin if you don't have credentials
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Single student row — fully isolated, no parent re-render ──
// Uses local state so clicking Present/Absent/Leave/Late is instant
const StudentRow = ({ student, initialStatus, initialRemark, canMark, onStatusChange, onRemarkChange, index }) => {
  const [status, setStatus] = useState(initialStatus || 'absent');
  const [remark, setRemark] = useState(initialRemark || '');
  const remarkRef = useRef(null);

  // Sync if parent resets (e.g. mark all)
  useEffect(() => {
    if (initialStatus && initialStatus !== status) {
      setStatus(initialStatus);
    }
  }, [initialStatus]);

  const handleStatus = (s) => {
    if (!canMark) return;
    setStatus(s);
    onStatusChange(student.student_roll_number || student.student_name, s);
  };

  const cfg = SC[status] || SC.absent;

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
      borderBottom:'1px solid #F1F5F9',
      background: status==='present'?'#FAFFFE': status==='absent'?'#FFFAFA': status==='leave'?'#FFFBF0': status==='late'?'#FAF5FF':'#fff',
      transition:'background 0.15s',
    }}>
      {/* Index */}
      <div style={{ width:28, fontSize:11, color:'#9CA3AF', fontWeight:600, flexShrink:0, textAlign:'center' }}>{index+1}</div>
      {/* Roll no */}
      <div style={{ width:110, flexShrink:0 }}>
        {student.student_roll_number
          ? <code style={{ fontSize:11, fontWeight:800, color:'#4F8EF7', background:'#EFF6FF', padding:'2px 7px', borderRadius:4 }}>{student.student_roll_number}</code>
          : <span style={{ fontSize:10, color:'#D1D5DB' }}>—</span>}
      </div>
      {/* Name */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{student.student_name}</div>
        {student.student_username && <div style={{ fontSize:10, color:'#9CA3AF' }}>@{student.student_username}</div>}
      </div>
      {/* Status buttons */}
      <div style={{ display:'flex', gap:4, flexShrink:0 }}>
        {['present','absent','leave','late'].map(s => (
          <button key={s} onClick={() => handleStatus(s)} disabled={!canMark}
            style={{
              width:68, padding:'6px 0', borderRadius:7, fontSize:10, fontWeight:800,
              border:`1.5px solid ${status===s ? SC[s].border : '#E5E7EB'}`,
              background: status===s ? SC[s].bg : '#fff',
              color:      status===s ? SC[s].color : '#9CA3AF',
              cursor:     canMark ? 'pointer' : 'not-allowed',
              transition: 'all 0.1s',
            }}>
            {SC[s].emoji} {SC[s].label}
          </button>
        ))}
      </div>
      {/* Remark input — only for leave, uses uncontrolled input */}
      {status === 'leave' && canMark && (
        <input
          ref={remarkRef}
          defaultValue={remark}
          placeholder="Leave reason..."
          onChange={e => {
            setRemark(e.target.value);
            onRemarkChange(student.student_roll_number || student.student_name, e.target.value);
          }}
          style={{ marginLeft:6, padding:'5px 10px', borderRadius:7, border:'1px solid #FED7AA', fontSize:11, color:'#92400E', background:'#FFF7ED', outline:'none', width:150 }}
        />
      )}
    </div>
  );
};

// ── OUTER SHELL — just provides LMSLayout + LMSPageGuard context ─
// useLMSAccess MUST be called INSIDE LMSLayout because LMSLayout
// wraps LMSAccessProvider. So we split into outer + inner.
export default function LMSAttendance() {
  return (
    <LMSLayout>
      <LMSPageGuard pageKey="attendance">
        <LMSAttendanceInner />
      </LMSPageGuard>
    </LMSLayout>
  );
}

// ── INNER — safe to call useLMSAccess here (inside provider) ─────
function LMSAttendanceInner() {
  const { account, institution } = useLMSAuth();
  const { isAdmin }              = useLMSAccess();

  // Teacher session (non-admin)
  const [teacherSession, setTeacherSession] = useState(() => {
    try {
      const stored = sessionStorage.getItem('lms_teacher_att_session');
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (parsed.institutionId !== institution?.id) return null;
      return parsed;
    } catch { return null; }
  });

  const [view,       setView]       = useState('mark');
  const [allClasses, setAllClasses] = useState([]);
  const [selClass,   setSelClass]   = useState(null);
  const [selDate,    setSelDate]    = useState(() => new Date().toISOString().split('T')[0]);
  const [session,    setSession]    = useState(null);
  const [students,   setStudents]   = useState([]);
  const [records,    setRecords]    = useState({});   // key→status (source of truth for save)
  const [remarks,    setRemarks]    = useState({});   // key→remark
  const [markAllTrigger, setMarkAllTrigger] = useState(null); // { status, ts }
  const [settings,   setSettings]   = useState(null);
  const [settForm,   setSettForm]   = useState(null);
  const [totalData,  setTotalData]  = useState([]);
  const [sessions,   setSessions]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [sessLoad,   setSessLoad]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [totalLoad,  setTotalLoad]  = useState(false);
  const [sessListLoad,setSessListLoad]=useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [showHoliday,setShowHoliday]=useState(false);
  const holidayRef = useRef(null);
  // Search — debounced
  const [searchInput,setSearchInput]=useState('');
  const [search,     setSearch]     =useState('');
  const [totalSearchInput,setTotalSearchInput]=useState('');
  const [totalSearch,setTotalSearch]=useState('');
  const sTimer = useRef(null);
  const tTimer = useRef(null);
  const handleSearch = v => { setSearchInput(v); clearTimeout(sTimer.current); sTimer.current=setTimeout(()=>setSearch(v),200); };
  const handleTotalSearch = v => { setTotalSearchInput(v); clearTimeout(tTimer.current); tTimer.current=setTimeout(()=>setTotalSearch(v),200); };

  // Classes available (admin=all, teacher=assigned only)
  const myClasses = useMemo(() => {
    if (isAdmin) return allClasses;
    if (!teacherSession?.classes) return [];
    return teacherSession.classes;
  }, [isAdmin, allClasses, teacherSession]);

  useEffect(() => { if (institution?.id) init(); }, [institution?.id]);
  useEffect(() => { if (selClass) loadSession(); }, [selClass, selDate]);
  useEffect(() => { if (view==='total' && selClass) loadTotal(); }, [view, selClass]);
  useEffect(() => { if (view==='sessions' && selClass) loadSessions(); }, [view, selClass]);

  const init = async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        supabase.from('lms_attendance_settings').select('*').eq('institution_id',institution.id).maybeSingle(),
        supabase.from('lms_classes').select('*').eq('institution_id',institution.id).neq('is_active',false).order('name'),
      ]);
      setSettings(sRes.data);
      setAllClasses(cRes.data || []);
      if (isAdmin && cRes.data?.length) setSelClass(cRes.data[0]);
      else if (teacherSession?.classes?.length) setSelClass(teacherSession.classes[0]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadSession = useCallback(async () => {
    if (!selClass) return;
    setSessLoad(true); setError('');
    try {
      const dayName = DAYS[new Date(selDate+'T12:00:00').getDay()];
      const isSat   = dayName==='Saturday';
      const isSun   = dayName==='Sunday';
      if ((isSat&&settings?.saturday_off)||(isSun&&settings?.sunday_off)) {
        setSession({ is_holiday:true, holiday_reason:`${dayName} — Off Day` });
        setStudents([]); setRecords({}); setRemarks({});
        setSessLoad(false); return;
      }

      // Load students FIRST and set them immediately so they always show
      const { data: studs, error: studsErr } = await supabase.from('lms_class_students')
        .select('*').eq('class_id', selClass.id).eq('enrollment_status','active')
        .order('student_roll_number', { ascending:true, nullsFirst:false });

      if (studsErr) console.error('Load students error:', studsErr);
      // Set students immediately — they show regardless of session status
      setStudents(studs||[]);

      // Find or create session
      let { data: sess } = await supabase.from('lms_attendance_sessions')
        .select('*').eq('class_id',selClass.id).eq('session_date',selDate).maybeSingle();

      if (!sess) {
        const { data: ns, error: sessErr } = await supabase.from('lms_attendance_sessions').insert({
          institution_id: institution.id,
          class_id:       selClass.id,
          session_date:   selDate,
          day_name:       dayName,
          session_type:   'regular',
          total_students: studs?.length||0,
        }).select().single();
        if (sessErr) {
          console.error('Session create error:', sessErr);
          setError('Session error: ' + sessErr.message + '. Run lms_attendance_final_fix.sql in Supabase first.');
          setSessLoad(false); return;
        }
        if (!ns) {
          setError('Session could not be created. Run lms_attendance_final_fix.sql in Supabase.');
          setSessLoad(false); return;
        }
        sess = ns;
        // Insert default absent records
        if (studs?.length && ns) {
          const rows = studs.map(s => ({
            institution_id:      institution.id,
            session_id:          ns.id,
            class_id:            selClass.id,
            student_id:          s.student_id||null,
            student_name:        s.student_name,
            student_roll_number: s.student_roll_number||null,
            student_username:    s.student_username||null,
            status:              'absent',
            session_date:        selDate,
          }));
          await supabase.from('lms_attendance_records').insert(rows);
        }
      }

      // Load records
      const { data: recs } = await supabase.from('lms_attendance_records')
        .select('*').eq('session_id', sess.id);

      const recMap = {}; const remMap = {};
      for (const r of (recs||[])) {
        const k = r.student_roll_number || r.student_name;
        recMap[k] = r.status;
        if (r.remarks) remMap[k] = r.remarks;
      }

      setSession(sess);
      // students already set above — just update records
      setRecords(recMap);
      setRemarks(remMap);
    } catch(e) { setError('Session load failed: '+e.message); }
    finally { setSessLoad(false); }
  }, [selClass, selDate, institution?.id, settings]);

  const loadTotal = async () => {
    setTotalLoad(true);
    try {
      const { data } = await supabase.rpc('get_class_attendance_summary',{
        p_class_id:       selClass.id,
        p_institution_id: institution.id,
      });
      setTotalData(Array.isArray(data)?data:[]);
    } catch(e){console.error(e);}
    finally{setTotalLoad(false);}
  };

  const loadSessions = async () => {
    setSessListLoad(true);
    try {
      const { data } = await supabase.from('lms_attendance_sessions').select('*')
        .eq('class_id',selClass.id).order('session_date',{ascending:false}).limit(90);
      setSessions(data||[]);
    } catch(e){console.error(e);}
    finally{setSessListLoad(false);}
  };

  // Mark all — triggers re-sync in StudentRow via useEffect
  const markAll = (status) => {
    const newRec = {};
    students.forEach(s=>{ newRec[s.student_roll_number||s.student_name]=status; });
    setRecords(newRec);
    setMarkAllTrigger({ status, ts: Date.now() });
  };

  // Called by StudentRow on button click
  const onStatusChange = useCallback((key, status) => {
    setRecords(r=>({...r,[key]:status}));
  },[]);

  const onRemarkChange = useCallback((key, val) => {
    setRemarks(r=>({...r,[key]:val}));
  },[]);

  const saveAttendance = async () => {
    if (!session||session.is_finalized||session.is_holiday) return;
    setSaving(true); setError('');
    try {
      const markerName = isAdmin
        ? (account?.lms_username||'Admin')
        : (teacherSession?.full_name||'Teacher');

      // Upsert each record individually to avoid constraint issues
      for (const s of students) {
        const key    = s.student_roll_number||s.student_name;
        const status = records[key]||'absent';
        const remark = remarks[key]||null;
        const base   = {
          institution_id:      institution.id,
          session_id:          session.id,
          class_id:            selClass.id,
          student_id:          s.student_id||null,
          student_name:        s.student_name,
          student_roll_number: s.student_roll_number||null,
          student_username:    s.student_username||null,
          status,
          remarks:             remark,
          session_date:        selDate,
          marked_at:           new Date().toISOString(),
        };
        // Try update first, then insert
        const { data: existing } = await supabase.from('lms_attendance_records')
          .select('id').eq('session_id',session.id)
          .eq(s.student_roll_number?'student_roll_number':'student_name', s.student_roll_number||s.student_name)
          .maybeSingle();
        if (existing) {
          await supabase.from('lms_attendance_records').update({ status, remarks:remark, marked_at:base.marked_at }).eq('id',existing.id);
        } else {
          await supabase.from('lms_attendance_records').insert(base);
        }
      }

      await supabase.from('lms_attendance_sessions').update({
        marked_by_name: markerName,
        marked_at:      new Date().toISOString(),
      }).eq('id',session.id);

      setSuccess(`✅ Attendance saved for ${students.length} students!`);
      setTimeout(()=>setSuccess(''),5000);
      await loadSession();

      // Notify absent students
      const absentIds = students.filter(s=>(records[s.student_roll_number||s.student_name]||'absent')==='absent'&&s.student_id).map(s=>s.student_id);
      if (absentIds.length) {
        try {
          await notifyCustom(absentIds,'⚠️ Marked Absent',
            `Your attendance for ${new Date(selDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'})} in ${selClass.name} is Absent.`,
            {screen:'attendance'});
        } catch(_){}
      }
    } catch(e){ setError('Save failed: '+e.message); }
    finally{ setSaving(false); }
  };

  const markHoliday = async () => {
    const reason = holidayRef.current?.value||'';
    try {
      await supabase.from('lms_attendance_sessions').update({ is_holiday:true, session_type:'holiday', holiday_reason:reason }).eq('id',session.id);
      await supabase.from('lms_attendance_records').update({ status:'holiday' }).eq('session_id',session.id);
      setShowHoliday(false);
      setSuccess('Day marked as holiday');
      setTimeout(()=>setSuccess(''),3000);
      await loadSession();
    } catch(e){ setError(e.message); }
  };

  const saveSettings = async () => {
    try {
      await supabase.from('lms_attendance_settings').upsert({ institution_id:institution.id, ...settForm });
      setSettings({...settForm});
      setSuccess('Settings saved!');
      setTimeout(()=>setSuccess(''),3000);
    } catch(e){ setError(e.message); }
  };

  const isWithinWindow = () => {
    if (!settings) return true;
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    return hhmm>=settings.marking_start_time && hhmm<=settings.marking_end_time;
  };

  const canMark = !session?.is_finalized && !session?.is_holiday &&
    (isAdmin || isWithinWindow());

  const filteredStudents = useMemo(()=>
    students.filter(s=>!search.trim()||
      (s.student_name||'').toLowerCase().includes(search.toLowerCase())||
      (s.student_roll_number||'').toLowerCase().includes(search.toLowerCase()))
  ,[students,search]);

  const filteredTotal = useMemo(()=>
    totalData.filter(s=>!totalSearch.trim()||
      (s.student_name||'').toLowerCase().includes(totalSearch.toLowerCase())||
      (s.student_roll_number||'').toLowerCase().includes(totalSearch.toLowerCase()))
  ,[totalData,totalSearch]);

  const presentCount = useMemo(()=>Object.values(records).filter(v=>v==='present').length,[records]);
  const absentCount  = useMemo(()=>Object.values(records).filter(v=>v==='absent').length,[records]);
  const leaveCount   = useMemo(()=>Object.values(records).filter(v=>v==='leave').length,[records]);
  const lateCount    = useMemo(()=>Object.values(records).filter(v=>v==='late').length,[records]);

  const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #E5E7EB', fontSize:13, boxSizing:'border-box', outline:'none', color:'#111827' };

  // ── Not logged in as teacher (non-admin) ──────────
  if (!isAdmin && !teacherSession) {
    return (
      <TeacherLoginScreen
        institutionId={institution?.id}
        onLogin={data => { setTeacherSession(data); if(data.classes?.length) setSelClass(data.classes[0]); }}
      />
    );
  }

  if (loading) return <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div>;

  // Teacher has no classes assigned
  if (!isAdmin && myClasses.length===0) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:'60vh',padding:32}}>
      <div style={{fontSize:64,marginBottom:16}}>📋</div>
      <div style={{fontSize:20,fontWeight:800,color:'#1E293B',marginBottom:8}}>No Class Assigned</div>
      <div style={{fontSize:14,color:'#6B7280',textAlign:'center',maxWidth:360}}>
        You are not assigned as class teacher of any class. Please contact the institution admin to assign you a class.
      </div>
      <div style={{marginTop:12,fontSize:13,color:'#94A3B8'}}>Logged in as: <strong>{teacherSession?.full_name}</strong></div>
      <button onClick={()=>{sessionStorage.removeItem('lms_teacher_att_session');setTeacherSession(null);}}
        style={{marginTop:20,padding:'10px 24px',borderRadius:10,border:'1px solid #FECACA',background:'#FEF2F2',color:'#EF4444',fontSize:13,fontWeight:700,cursor:'pointer'}}>
        Switch Account
      </button>
    </div>
  );

  return (
    <>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Attendance</h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>
              {!isAdmin && teacherSession && <span>👤 <strong>{teacherSession.full_name}</strong> · <button onClick={()=>{sessionStorage.removeItem('lms_teacher_att_session');setTeacherSession(null);}} style={{background:'none',border:'none',color:'#EF4444',fontSize:12,cursor:'pointer',fontWeight:700}}>Switch</button> · </span>}
              {myClasses.length} class{myClasses.length!==1?'es':''} · {new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}
            </p>
          </div>
          <div style={{display:'flex',gap:8}}>
            {isAdmin && (
              <button onClick={()=>{setView('settings');setSettForm(settings||{marking_start_time:'08:00',marking_end_time:'23:59',saturday_off:true,sunday_off:true,late_minutes:15});}}
                style={{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                <Settings size={15}/> Settings
              </button>
            )}
          </div>
        </div>

        {success && <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}
        {error   && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#991B1B'}}>{error}<button onClick={()=>setError('')} style={{float:'right',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>}

        {/* Tabs — admin gets more tabs */}
        <div style={{display:'flex',background:'#F1F5F9',borderRadius:12,padding:4,marginBottom:20,width:'fit-content',gap:2,flexWrap:'wrap'}}>
          {[
            {k:'mark',    l:'📋 Mark'},
            ...(isAdmin?[{k:'total',l:'📊 Summary'},{k:'sessions',l:'📅 Sessions'},{k:'settings',l:'⚙️ Settings'}]:[]),
          ].map(t=>(
            <button key={t.k} onClick={()=>setView(t.k)}
              style={{padding:'8px 16px',borderRadius:9,border:'none',background:view===t.k?'#fff':'transparent',color:view===t.k?'#1E293B':'#64748B',fontWeight:700,fontSize:13,cursor:'pointer',boxShadow:view===t.k?'0 1px 4px rgba(0,0,0,0.08)':'none'}}>
              {t.l}
            </button>
          ))}
        </div>

        {/* ── SETTINGS ── */}
        {view==='settings' && settForm && (
          <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',padding:24,maxWidth:560}}>
            <div style={{fontWeight:800,color:'#111827',fontSize:16,marginBottom:20}}>⚙️ Attendance Settings</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
              {[['Marking Opens At','marking_start_time','time'],['Marking Closes At','marking_end_time','time'],['Late After (minutes)','late_minutes','number']].map(([label,key,type])=>(
                <div key={key}>
                  <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>{label}</label>
                  <input type={type} value={settForm[key]||''} onChange={e=>setSettForm({...settForm,[key]:type==='number'?Number(e.target.value):e.target.value})} style={inp}/>
                </div>
              ))}
              <div style={{gridColumn:'span 2'}}>
                <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:8}}>Off Days</label>
                <div style={{display:'flex',gap:10}}>
                  {[['saturday_off','Saturday'],['sunday_off','Sunday']].map(([key,label])=>(
                    <label key={key} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'8px 14px',borderRadius:8,border:`1.5px solid ${settForm[key]?'#4F8EF7':'#E5E7EB'}`,background:settForm[key]?'#EFF6FF':'#fff'}}>
                      <input type="checkbox" checked={!!settForm[key]} onChange={e=>setSettForm({...settForm,[key]:e.target.checked})} style={{width:16,height:16,accentColor:'#4F8EF7'}}/>
                      <span style={{fontSize:13,fontWeight:600,color:settForm[key]?'#1D4ED8':'#374151'}}>{label} Off</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Academic Year Start</label>
                <input type="date" value={settForm.academic_year_start||''} onChange={e=>setSettForm({...settForm,academic_year_start:e.target.value})} style={inp}/>
              </div>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Academic Year End</label>
                <input type="date" value={settForm.academic_year_end||''} onChange={e=>setSettForm({...settForm,academic_year_end:e.target.value})} style={inp}/>
              </div>
            </div>
            {/* Set teacher PINs */}
            <div style={{marginTop:24,borderTop:'1px solid #E5E7EB',paddingTop:20}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:14,marginBottom:6}}>🔑 Teacher PINs for Attendance</div>
              <div style={{fontSize:12,color:'#6B7280',marginBottom:14}}>Set a username and PIN for each teacher so they can log in to mark attendance. The username is auto-generated when you add a teacher — you only need to set the PIN.</div>
              <TeacherPINManager institutionId={institution?.id}/>
            </div>
            <button onClick={saveSettings}
              style={{marginTop:20,width:'100%',padding:'12px 0',borderRadius:10,border:'none',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer'}}>
              Save Settings
            </button>
          </div>
        )}

        {/* ── TOTAL SUMMARY ── */}
        {view==='total' && (
          <>
            <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
              <select value={selClass?.id||''} onChange={e=>{const c=allClasses.find(x=>x.id===e.target.value);setSelClass(c);}} style={{...inp,width:'auto',minWidth:200}}>
                {allClasses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div style={{position:'relative',flex:1,maxWidth:320}}>
                <Search size={14} color="#9CA3AF" style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                <input value={totalSearchInput} onChange={e=>handleTotalSearch(e.target.value)} placeholder="Search student or roll no..." style={{...inp,paddingLeft:32}}/>
              </div>
              <div style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filteredTotal.length} students</div>
            </div>
            {totalData.length>0 && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:10,marginBottom:16}}>
                {[
                  {l:'Students',    v:totalData.length, c:'#4F8EF7'},
                  {l:'Avg Attend.', v:`${Math.round(totalData.reduce((a,s)=>a+(s.percentage||0),0)/totalData.length)}%`, c:'#10B981'},
                  {l:'Below 75%',   v:totalData.filter(s=>(s.percentage||0)<75).length, c:'#EF4444'},
                  {l:'Sessions',    v:totalData[0]?.total_sessions||0, c:'#7C3AED'},
                ].map(s=>(
                  <div key={s.l} style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',padding:'12px 16px'}}>
                    <div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div>
                    <div style={{fontSize:11,color:'#6B7280'}}>{s.l}</div>
                  </div>
                ))}
              </div>
            )}
            {totalLoad ? <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div> :
            filteredTotal.length===0 ? <div style={{padding:48,textAlign:'center',background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',color:'#9CA3AF'}}>No attendance data yet for this class</div> : (
              <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
                  <thead>
                    <tr style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB'}}>
                      {['#','Roll No','Student','Sessions','✅ Present','❌ Absent','🟡 Leave','⏰ Late','Percentage'].map(h=>(
                        <th key={h} style={{padding:'11px 14px',textAlign:'left',fontSize:11,fontWeight:800,color:'#6B7280',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTotal.map((s,i)=>{
                      const pct=s.percentage||0;
                      const pc=pct>=75?'#10B981':pct>=60?'#F59E0B':'#EF4444';
                      return(
                        <tr key={i} style={{borderBottom:i<filteredTotal.length-1?'1px solid #F1F5F9':'none',background:pct<75?'#FFF5F5':'transparent'}}>
                          <td style={{padding:'11px 14px',fontSize:12,color:'#9CA3AF',fontWeight:600}}>{i+1}</td>
                          <td style={{padding:'11px 14px'}}>
                            {s.student_roll_number?<code style={{fontSize:12,fontWeight:800,color:'#4F8EF7',background:'#EFF6FF',padding:'3px 8px',borderRadius:5}}>{s.student_roll_number}</code>:<span style={{color:'#D1D5DB'}}>—</span>}
                          </td>
                          <td style={{padding:'11px 14px'}}>
                            <div style={{fontWeight:700,color:'#111827',fontSize:13}}>{s.student_name}</div>
                            {s.student_username&&<div style={{fontSize:10,color:'#9CA3AF'}}>@{s.student_username}</div>}
                          </td>
                          <td style={{padding:'11px 14px',fontSize:13,color:'#374151',fontWeight:700}}>{s.total_sessions}</td>
                          <td style={{padding:'11px 14px'}}><span style={{fontSize:14,fontWeight:900,color:'#10B981'}}>{s.present}</span></td>
                          <td style={{padding:'11px 14px'}}><span style={{fontSize:14,fontWeight:900,color:'#EF4444'}}>{s.absent}</span></td>
                          <td style={{padding:'11px 14px'}}><span style={{fontSize:14,fontWeight:900,color:'#F59E0B'}}>{s.leave}</span></td>
                          <td style={{padding:'11px 14px'}}><span style={{fontSize:14,fontWeight:900,color:'#8B5CF6'}}>{s.late}</span></td>
                          <td style={{padding:'11px 14px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <div style={{flex:1,background:'#F1F5F9',borderRadius:20,height:8,overflow:'hidden',minWidth:60}}>
                                <div style={{height:'100%',width:`${pct}%`,background:pc,borderRadius:20}}/>
                              </div>
                              <span style={{fontSize:13,fontWeight:900,color:pc,minWidth:38}}>{pct}%</span>
                            </div>
                            {pct<75&&<div style={{fontSize:9,color:'#EF4444',fontWeight:700,marginTop:2}}>⚠️ BELOW 75%</div>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── SESSIONS LIST ── */}
        {view==='sessions' && (
          <>
            <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
              <select value={selClass?.id||''} onChange={e=>{const c=allClasses.find(x=>x.id===e.target.value);setSelClass(c);}} style={{...inp,width:'auto',minWidth:200}}>
                {allClasses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {sessListLoad?<div style={{padding:32,textAlign:'center',color:'#9CA3AF'}}>Loading...</div>:
            sessions.length===0?<div style={{padding:48,textAlign:'center',background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',color:'#9CA3AF'}}>No sessions yet</div>:(
              <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:600}}>
                  <thead>
                    <tr style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB'}}>
                      {['Date','Day','Type','Present','Absent','Leave','Total','Status','Marked By'].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:800,color:'#6B7280',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s,i)=>{
                      const pct=s.total_students>0?Math.round(s.present_count/s.total_students*100):0;
                      return(
                        <tr key={s.id} style={{borderBottom:i<sessions.length-1?'1px solid #F1F5F9':'none',background:s.is_holiday?'#F8FAFF':s.is_finalized?'#F5F3FF':'transparent'}}>
                          <td style={{padding:'10px 14px',fontSize:13,fontWeight:700,color:'#111827'}}>{s.session_date}</td>
                          <td style={{padding:'10px 14px',fontSize:12,color:'#6B7280'}}>{s.day_name}</td>
                          <td style={{padding:'10px 14px'}}>
                            <span style={{fontSize:10,fontWeight:700,color:s.is_holiday?'#64748B':'#4F8EF7',background:s.is_holiday?'#F1F5F9':'#EFF6FF',padding:'2px 8px',borderRadius:4}}>
                              {s.is_holiday?'🏖 Holiday':s.session_type}
                            </span>
                          </td>
                          <td style={{padding:'10px 14px',fontSize:13,fontWeight:800,color:'#10B981'}}>{s.present_count}</td>
                          <td style={{padding:'10px 14px',fontSize:13,fontWeight:800,color:'#EF4444'}}>{s.absent_count}</td>
                          <td style={{padding:'10px 14px',fontSize:13,fontWeight:800,color:'#F59E0B'}}>{s.leave_count}</td>
                          <td style={{padding:'10px 14px',fontSize:13,color:'#374151'}}>{s.total_students}</td>
                          <td style={{padding:'10px 14px'}}>
                            {s.is_finalized?<span style={{fontSize:10,fontWeight:700,color:'#5B21B6',background:'#F5F3FF',padding:'2px 8px',borderRadius:4}}>🔒 Final</span>
                             :s.marked_at?<span style={{fontSize:10,fontWeight:700,color:'#10B981',background:'#F0FDF4',padding:'2px 8px',borderRadius:4}}>✅ {pct}%</span>
                             :<span style={{fontSize:10,fontWeight:700,color:'#F59E0B',background:'#FFF7ED',padding:'2px 8px',borderRadius:4}}>⏳ Pending</span>}
                          </td>
                          <td style={{padding:'10px 14px',fontSize:11,color:'#9CA3AF'}}>{s.marked_by_name||'—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── MARK ATTENDANCE ── */}
        {view==='mark' && (
          <>
            {/* Controls bar */}
            <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:16,marginBottom:16}}>
              <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end'}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:'#6B7280',display:'block',marginBottom:4}}>CLASS</label>
                  <select value={selClass?.id||''} onChange={e=>setSelClass(myClasses.find(c=>c.id===e.target.value))}
                    style={{...inp,width:'auto',minWidth:180,padding:'7px 12px'}}>
                    {myClasses.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:'#6B7280',display:'block',marginBottom:4}}>DATE</label>
                  <input type="date" value={selDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={e=>setSelDate(e.target.value)}
                    style={{...inp,width:'auto',padding:'7px 12px'}}/>
                </div>
                <div style={{flex:1,minWidth:180}}>
                  <label style={{fontSize:11,fontWeight:700,color:'#6B7280',display:'block',marginBottom:4}}>SEARCH</label>
                  <div style={{position:'relative'}}>
                    <Search size={13} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                    <input value={searchInput} onChange={e=>handleSearch(e.target.value)} placeholder="Name or roll no..."
                      style={{...inp,paddingLeft:28,padding:'7px 12px 7px 28px'}}/>
                  </div>
                </div>
                {canMark && students.length>0 && (
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:'#6B7280',display:'block',marginBottom:4}}>MARK ALL</label>
                    <div style={{display:'flex',gap:6}}>
                      {['present','absent','leave'].map(s=>(
                        <button key={s} onClick={()=>markAll(s)}
                          style={{padding:'6px 12px',borderRadius:7,border:`1px solid ${SC[s].border}`,background:SC[s].bg,color:SC[s].color,fontSize:11,fontWeight:800,cursor:'pointer'}}>
                          {SC[s].emoji} All
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Banners */}
            {session?.is_holiday && (
              <div style={{background:'#F8FAFF',border:'1.5px solid #E2E8F0',borderRadius:12,padding:'14px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                <span style={{fontSize:24}}>🏖</span>
                <div><div style={{fontWeight:800,color:'#475569',fontSize:14}}>Holiday / Off Day</div><div style={{fontSize:12,color:'#94A3B8'}}>{session.holiday_reason||'No classes today'}</div></div>
              </div>
            )}
            {session?.is_finalized && (
              <div style={{background:'#F5F3FF',border:'1.5px solid #DDD6FE',borderRadius:12,padding:'14px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                <Lock size={18} color="#7C3AED"/>
                <div style={{fontWeight:700,color:'#5B21B6',fontSize:13}}>This session is finalized — read only.</div>
              </div>
            )}
            {!isAdmin&&!isWithinWindow()&&!session?.is_finalized&&!session?.is_holiday && (
              <div style={{background:'#FFF7ED',border:'1.5px solid #FED7AA',borderRadius:12,padding:'14px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                <Clock size={18} color="#F59E0B"/>
                <div><div style={{fontWeight:700,color:'#92400E',fontSize:13}}>Outside Marking Window</div><div style={{fontSize:12,color:'#B45309'}}>Marking allowed {settings?.marking_start_time} – {settings?.marking_end_time} only</div></div>
              </div>
            )}

            {/* Stats */}
            {!session?.is_holiday&&students.length>0&&(
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
                {[{l:'Total',v:students.length,c:'#4F8EF7',e:'👥'},{l:'Present',v:presentCount,c:'#10B981',e:'✅'},{l:'Absent',v:absentCount,c:'#EF4444',e:'❌'},{l:'Leave',v:leaveCount,c:'#F59E0B',e:'🟡'},{l:'Late',v:lateCount,c:'#8B5CF6',e:'⏰'}].map(s=>(
                  <div key={s.l} style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',padding:'12px 14px',textAlign:'center'}}>
                    <div style={{fontSize:18,marginBottom:4}}>{s.e}</div>
                    <div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div>
                    <div style={{fontSize:10,color:'#9CA3AF'}}>{s.l}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Sheet */}
            {sessLoad?<div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading attendance sheet...</div>:
            session?.is_holiday?null:
            filteredStudents.length===0?(
              <div style={{padding:48,textAlign:'center',background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',color:'#9CA3AF'}}>
                {students.length===0?'No students enrolled. Add students via Classes page.':'No students match search.'}
              </div>
            ):(
              <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'hidden'}}>
                {/* Sheet header */}
                <div style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB',padding:'10px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#374151'}}>
                    {selClass?.name} · {new Date(selDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric',year:'numeric'})}
                    {session?.marked_by_name&&<span style={{color:'#9CA3AF',fontWeight:400,marginLeft:8}}>· {session.marked_by_name}</span>}
                  </div>
                  {canMark&&(
                    <button onClick={()=>setShowHoliday(true)}
                      style={{padding:'5px 12px',borderRadius:7,border:'1px solid #E2E8F0',background:'#F8FAFF',color:'#475569',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                      🏖 Mark Holiday
                    </button>
                  )}
                </div>

                {/* Student rows — each is isolated, no typing lag */}
                {filteredStudents.map((s,i)=>(
                  <StudentRow
                    key={s.id}
                    student={s}
                    index={i}
                    initialStatus={records[s.student_roll_number||s.student_name]||(markAllTrigger?.status)}
                    initialRemark={remarks[s.student_roll_number||s.student_name]}
                    canMark={canMark}
                    onStatusChange={onStatusChange}
                    onRemarkChange={onRemarkChange}
                  />
                ))}

                {/* Save */}
                {canMark&&(
                  <div style={{padding:16,borderTop:'1px solid #E5E7EB'}}>
                    <button onClick={saveAttendance} disabled={saving}
                      style={{width:'100%',padding:'13px 0',borderRadius:11,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#10B981,#059669)',color:saving?'#9CA3AF':'#fff',fontWeight:800,fontSize:14,cursor:saving?'wait':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:10}}>
                      <Save size={18}/>{saving?'Saving...':`Save Attendance (${students.length} students)`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Holiday modal */}
        {showHoliday&&(
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
            <div style={{background:'#fff',borderRadius:18,width:'100%',maxWidth:420,padding:24}}>
              <div style={{fontWeight:800,color:'#111827',fontSize:17,marginBottom:16}}>🏖 Mark as Holiday</div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Reason</label>
                <input ref={holidayRef} defaultValue="" placeholder="e.g. Independence Day, Eid..." style={{...inp}}/>
              </div>
              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>setShowHoliday(false)} style={{flex:1,padding:'11px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
                <button onClick={markHoliday} style={{flex:2,padding:'11px 0',borderRadius:10,border:'none',background:'#64748B',color:'#fff',fontWeight:700,cursor:'pointer'}}>Mark as Holiday</button>
              </div>
            </div>
          </div>
        )}

    </>
  );
}

// ── Teacher PIN Manager (inside Settings) ─────────────
function TeacherPINManager({ institutionId }) {
  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving] = useState({});
  const [msgs, setMsgs] = useState({});

  useEffect(() => {
    if (!institutionId) return;
    supabase.from('lms_teachers').select('id,full_name,teacher_username,teacher_pin,designation,department')
      .eq('institution_id', institutionId).eq('status','active').order('full_name')
      .then(({data}) => setTeachers(data||[]));
  }, [institutionId]);

  const savePin = async (teacher, username, pin) => {
    setSaving(s=>({...s,[teacher.id]:true}));
    try {
      await supabase.from('lms_teachers').update({
        teacher_username: username.trim()||teacher.teacher_username,
        teacher_pin:      pin.trim()||null,
      }).eq('id', teacher.id);
      setMsgs(m=>({...m,[teacher.id]:'✅ Saved'}));
      setTimeout(()=>setMsgs(m=>({...m,[teacher.id]:''})),3000);
      // Refresh
      const {data} = await supabase.from('lms_teachers').select('id,full_name,teacher_username,teacher_pin,designation,department').eq('institution_id',institutionId).eq('status','active').order('full_name');
      setTeachers(data||[]);
    } catch(e) { setMsgs(m=>({...m,[teacher.id]:'❌ '+e.message})); }
    finally { setSaving(s=>({...s,[teacher.id]:false})); }
  };

  if (!teachers.length) return <div style={{fontSize:13,color:'#9CA3AF'}}>No active teachers found. Add teachers from the Teachers page first.</div>;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {teachers.map(t=>(
        <TeacherPINRow key={t.id} teacher={t} onSave={savePin} saving={saving[t.id]} msg={msgs[t.id]}/>
      ))}
    </div>
  );
}

// Each teacher PIN row — isolated, no lag
function TeacherPINRow({ teacher, onSave, saving, msg }) {
  const uRef = useRef(null);
  const pRef = useRef(null);
  const inp = { padding:'7px 10px', borderRadius:7, border:'1.5px solid #E5E7EB', fontSize:12, outline:'none', color:'#111827', width:'100%', boxSizing:'border-box' };
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',background:'#F9FAFB',borderRadius:10,border:'1px solid #E5E7EB',flexWrap:'wrap'}}>
      <div style={{flex:1,minWidth:120}}>
        <div style={{fontWeight:700,color:'#111827',fontSize:13}}>{teacher.full_name}</div>
        <div style={{fontSize:11,color:'#9CA3AF'}}>{teacher.designation} · {teacher.department}</div>
      </div>
      <div style={{flex:1,minWidth:130}}>
        <label style={{fontSize:10,fontWeight:700,color:'#6B7280',display:'block',marginBottom:3}}>USERNAME</label>
        <input ref={uRef} defaultValue={teacher.teacher_username||''} placeholder="username" style={inp}/>
      </div>
      <div style={{flex:1,minWidth:110}}>
        <label style={{fontSize:10,fontWeight:700,color:'#6B7280',display:'block',marginBottom:3}}>PIN (4-8 digits)</label>
        <input ref={pRef} defaultValue={teacher.teacher_pin||''} placeholder="set PIN" type="password" style={inp}/>
      </div>
      <div style={{flexShrink:0}}>
        <div style={{fontSize:10,color:'#10B981',fontWeight:700,marginBottom:3,minHeight:13}}>{msg||''}</div>
        <button onClick={()=>onSave(teacher,uRef.current?.value||'',pRef.current?.value||'')} disabled={saving}
          style={{padding:'7px 14px',borderRadius:7,border:'none',background:saving?'#E5E7EB':'#4F8EF7',color:saving?'#9CA3AF':'#fff',fontSize:12,fontWeight:700,cursor:saving?'wait':'pointer'}}>
          {saving?'...':'Save'}
        </button>
      </div>
      <div style={{flexShrink:0}}>
        <div style={{fontSize:10,color:'#9CA3AF',marginBottom:3}}>Has PIN</div>
        <span style={{fontSize:11,fontWeight:700,color:teacher.teacher_pin?'#10B981':'#EF4444'}}>
          {teacher.teacher_pin?'✅ Yes':'❌ No'}
        </span>
      </div>
    </div>
  );
}